import { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle, ArrowRight, Building2, CheckCircle2, ChevronRight, ClipboardList,
  History, ListChecks, Package, RefreshCw, Truck, User, UserCheck, Boxes,
} from "lucide-react";
import { Reveal, Eyebrow, SectionTitle } from "../Reveal";

const STATUS = {
  ok: { label: "Stock : OK", cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10" },
  reappro: { label: "Stock : À réapprovisionner", cls: "text-amber-400 border-amber-400/40 bg-amber-400/10" },
  faible: { label: "Stock : Stock faible", cls: "text-red-400 border-red-400/40 bg-red-400/10" },
};

const TRUCKS = [
  { id: "01", name: "Camion 01", tech: "—", status: "ok", boxes: 3, faibles: 0,
    stock: [
      { ref: "WAGO 221", qty: 64, state: "ok" },
      { ref: "VIS 4×48", qty: 210, state: "ok" },
      { ref: "COLLIERS", qty: 88, state: "ok" },
    ] },
  { id: "02", name: "Camion 02", tech: "Jean", status: "reappro", boxes: 2, faibles: 4,
    stock: [
      { ref: "WAGO 221", qty: 18, state: "faible" },
      { ref: "VIS 4×48", qty: 124, state: "ok" },
      { ref: "CHEVILLES 6 MM", qty: 27, state: "faible" },
      { ref: "RACCORD 1/2\"", qty: 6, state: "critique" },
    ] },
  { id: "03", name: "Camion 03", tech: "—", status: "faible", boxes: 3, faibles: 7,
    stock: [
      { ref: "VIS 4×48", qty: 9, state: "critique" },
      { ref: "COLLIERS", qty: 14, state: "faible" },
      { ref: "ÉCROUS M6", qty: 22, state: "faible" },
    ] },
  { id: "04", name: "Camion 04", tech: "—", status: "ok", boxes: 2, faibles: 0,
    stock: [
      { ref: "RONDELLES", qty: 180, state: "ok" },
      { ref: "GAINES ICTA", qty: 42, state: "ok" },
    ] },
];

const REAPPRO = [
  { ref: "WAGO 221", estime: 18, min: 25, prevoir: 50, level: "faible" },
  { ref: "CHEVILLES 6 MM", estime: 27, min: 30, prevoir: 100, level: "faible" },
  { ref: "RACCORD 1/2\"", estime: 6, min: 10, prevoir: 20, level: "critique" },
];

const LISTE = [
  { truck: "Camion 01", items: [["Wago 221", 50]] },
  { truck: "Camion 02", items: [["Wago 221", 50], ["Chevilles 6 mm", 100], ["Raccord 1/2\"", 20]] },
  { truck: "Camion 03", items: [["Vis 4×48", 100], ["Colliers", 50]] },
];

const TOTAUX = [["Wago 221", 100], ["Chevilles 6 mm", 100], ["Raccord 1/2\"", 20], ["Vis 4×48", 100], ["Colliers", 50]];

const WORKFLOW = [
  { icon: AlertTriangle, label: "Stock faible détecté" },
  { icon: ClipboardList, label: "Liste de réapprovisionnement créée" },
  { icon: UserCheck, label: "Responsable valide la liste" },
  { icon: Package, label: "Pièces préparées" },
  { icon: Truck, label: "Pièces attribuées au véhicule" },
  { icon: RefreshCw, label: "Stock du véhicule mis à jour" },
];

const STATUTS = ["À surveiller", "À réapprovisionner", "Liste créée", "Préparation en cours", "Prêt à charger", "Réapprovisionné"];

const HIERARCHIE = ["Entreprise", "Véhicules", "Techniciens", "STOCKBOX 8", "Compartiments", "Références", "Quantités estimées"];

const HISTORIQUE = [
  { date: "29/09", qui: "Camion 02", texte: "Wago 221 : stock faible détecté" },
  { date: "30/09", qui: "Responsable", texte: "Réapprovisionnement validé" },
  { date: "01/10", qui: "Camion 02", texte: "50 Wago ajoutés" },
];

const stateBadge = (state) => {
  if (state === "ok") return <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-emerald-400"><CheckCircle2 size={11} /> Stock OK</span>;
  if (state === "faible") return <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-400"><AlertTriangle size={11} /> Stock faible</span>;
  return <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-red-400"><AlertTriangle size={11} /> Réapprovisionnement</span>;
};

export default function Fleet() {
  const [selected, setSelected] = useState("02");
  const [mode, setMode] = useState("fiche");
  const truck = TRUCKS.find((t) => t.id === selected);

  return (
    <section id="flotte" className="py-16 sm:py-24 bg-[#141619]/40 border-y border-[#2E333D]/50" data-testid="fleet-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Vision — fonctionnalité prévue</Eyebrow>
          <SectionTitle>Gérez le stock de toute votre flotte</SectionTitle>
          <p className="mt-4 font-display text-xl sm:text-2xl font-semibold text-[#FFC700] uppercase tracking-wide">
            Un seul endroit pour suivre les stocks de vos véhicules et organiser leur réapprovisionnement.
          </p>
          <p className="mt-6 text-gray-400 max-w-3xl leading-relaxed">
            Pour les entreprises qui disposent de plusieurs véhicules et techniciens, STOCKBOX 8 a vocation
            à permettre de centraliser la gestion des petites pièces présentes dans chaque véhicule.
            Chaque STOCKBOX 8 pourra être associée à un véhicule et/ou à un technicien afin de visualiser
            les quantités estimées disponibles et d'identifier rapidement les besoins de réapprovisionnement.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 border border-[#2E333D] bg-[#0C0D0E] rounded-2xl overflow-hidden" data-testid="fleet-mockup">
            <div className="px-5 py-4 border-b border-[#2E333D] flex items-center justify-between">
              <p className="font-display font-bold uppercase tracking-wider text-[#FFC700]">Flotte</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-600">Interface fictive — fonctionnalité prévue</p>
            </div>
            <div className="grid md:grid-cols-[280px_1fr]">
              <div className="border-b md:border-b-0 md:border-r border-[#2E333D] p-3 space-y-2">
                {TRUCKS.map((t) => (
                  <button key={t.id} onClick={() => { setSelected(t.id); setMode("fiche"); }}
                    data-testid={`fleet-truck-${t.id}`}
                    className={`w-full text-left rounded-xl border p-4 transition-all ${selected === t.id ? "border-[#FFC700] bg-[#FFC700]/5" : "border-[#2E333D] bg-[#141619] hover:border-gray-500"}`}>
                    <div className="flex items-center gap-3">
                      <Truck size={18} className={selected === t.id ? "text-[#FFC700]" : "text-gray-500"} />
                      <span className="font-display font-bold uppercase text-[#F9FAFB]">{t.name}</span>
                    </div>
                    <div className={`mt-2 inline-block border rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${STATUS[t.status].cls}`}>
                      {STATUS[t.status].label}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">{t.boxes} STOCKBOX — Références faibles : {t.faibles}</p>
                  </button>
                ))}
              </div>

              <div className="p-5 sm:p-7">
                {mode === "fiche" ? (
                  <div data-testid="fleet-truck-detail">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="font-display text-2xl font-bold uppercase text-[#F9FAFB]">{truck.name}</h3>
                        <p className="mt-1 flex items-center gap-2 text-sm text-gray-400"><User size={14} className="text-[#FFC700]" /> Technicien : {truck.tech}</p>
                        <p className="mt-1 flex items-center gap-2 text-sm text-gray-400"><Boxes size={14} className="text-[#FFC700]" /> STOCKBOX 8 : {Array.from({ length: truck.boxes }, (_, i) => `BOX 0${i + 1}`).join(" — ")}</p>
                      </div>
                      <span className={`border rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${STATUS[truck.status].cls}`}>{STATUS[truck.status].label}</span>
                    </div>
                    <p className="mt-5 font-mono text-[10px] uppercase tracking-widest text-gray-500">Stock actuel estimé</p>
                    <div className="mt-3 space-y-2.5">
                      {truck.stock.map((s) => (
                        <div key={s.ref} className="flex items-center justify-between gap-3 border border-[#2E333D] bg-[#141619] rounded-lg px-4 py-3" data-testid={`fleet-stock-${s.ref.replace(/[^A-Z0-9]/gi, "-")}`}>
                          <span className="font-mono text-xs text-gray-300">{s.ref}</span>
                          <div className="text-right">
                            <span className={`font-display text-xl font-bold ${s.state === "ok" ? "text-[#F9FAFB]" : s.state === "faible" ? "text-amber-400" : "text-red-400"}`}>
                              ≈ {s.qty} <span className="text-xs font-semibold">pièces</span>
                            </span>
                            <div className="mt-0.5">{stateBadge(s.state)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-[11px] text-gray-600">Quantités estimées — jamais exactes. Exemple visuel.</p>
                    {truck.status !== "ok" && (
                      <button onClick={() => setMode("reappro")} data-testid="fleet-create-list-button"
                        className="mt-5 inline-flex items-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-xs sm:text-sm px-6 py-3 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] transition-colors">
                        <ListChecks size={16} /> Créer la liste de réapprovisionnement
                      </button>
                    )}
                  </div>
                ) : (
                  <div data-testid="fleet-reappro-view">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <h3 className="font-display text-2xl font-bold uppercase text-[#F9FAFB]">Réapprovisionnement — {truck.name}</h3>
                      <button onClick={() => setMode("fiche")} className="text-xs text-gray-500 hover:text-[#FFC700] transition-colors" data-testid="fleet-back-button">← Retour véhicule</button>
                    </div>
                    <div className="mt-4 space-y-2.5">
                      {REAPPRO.map((r) => (
                        <div key={r.ref} className="border border-[#2E333D] bg-[#141619] rounded-lg px-4 py-3" data-testid={`fleet-reappro-${r.ref.replace(/[^A-Z0-9]/gi, "-")}`}>
                          <div className="flex items-center gap-2">
                            {r.level === "critique" ? <AlertTriangle size={14} className="text-red-400" /> : <AlertTriangle size={14} className="text-amber-400" />}
                            <span className="font-mono text-xs text-gray-200 font-semibold">{r.ref}</span>
                          </div>
                          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                            <div><p className="font-mono text-[9px] uppercase tracking-widest text-gray-600">Stock estimé</p><p className="font-display text-lg font-bold text-[#F9FAFB]">{r.estime}</p></div>
                            <div><p className="font-mono text-[9px] uppercase tracking-widest text-gray-600">Stock minimum</p><p className="font-display text-lg font-bold text-gray-400">{r.min}</p></div>
                            <div><p className="font-mono text-[9px] uppercase tracking-widest text-gray-600">À prévoir</p><p className="font-display text-lg font-bold text-[#FFC700]">{r.prevoir}</p></div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-6 border border-[#FFC700]/40 bg-[#FFC700]/5 rounded-xl p-5" data-testid="fleet-shopping-list">
                      <p className="font-display text-xl font-bold uppercase text-[#FFC700]">Liste de réapprovisionnement</p>
                      <div className="mt-4 grid sm:grid-cols-3 gap-4">
                        {LISTE.map((l) => (
                          <div key={l.truck}>
                            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-400 mb-2">{l.truck}</p>
                            <ul className="space-y-1">
                              {l.items.map(([ref, qty]) => (
                                <li key={ref} className="text-sm text-gray-300">— {ref} × {qty}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 border-t border-[#FFC700]/30 pt-4">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-[#FFC700] mb-2">Total à préparer</p>
                        <div className="flex flex-wrap gap-2">
                          {TOTAUX.map(([ref, qty]) => (
                            <span key={ref} className="border border-[#2E333D] bg-[#0C0D0E] rounded-full px-3 py-1.5 text-xs text-gray-200 font-mono">{ref} : <strong className="text-[#FFC700]">{qty}</strong></span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="mt-3 text-[11px] text-gray-600">Exemple visuel — fonctionnalité prévue, quantités estimées.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full border border-[#2E333D] bg-[#0C0D0E] rounded-2xl p-6 sm:p-8" data-testid="fleet-workflow">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFC700] mb-6">Workflow prévu</p>
              <div className="space-y-3">
                {WORKFLOW.map((w, i) => (
                  <div key={w.label} className="flex items-center gap-4" data-testid={`fleet-workflow-step-${i}`}>
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-[#FFC700]/10 border border-[#FFC700]/30 flex items-center justify-center">
                      <w.icon size={16} className="text-[#FFC700]" />
                    </span>
                    <span className="font-mono text-[10px] text-gray-600 w-6">0{i + 1}</span>
                    <p className="text-sm text-gray-300 font-medium uppercase tracking-wide">{w.label}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {STATUTS.map((s) => (
                  <span key={s} className="border border-[#2E333D] rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">{s}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full border border-[#2E333D] bg-[#0C0D0E] rounded-2xl p-6 sm:p-8" data-testid="fleet-manager-view">
              <div className="flex items-center justify-between">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFC700]">Vue responsable — prévue</p>
                <Building2 size={18} className="text-gray-600" />
              </div>
              <h3 className="mt-4 font-display text-3xl font-bold uppercase text-[#F9FAFB]">Ma flotte</h3>
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[["12", "véhicules"], ["8", "techniciens"], ["24", "STOCKBOX 8"]].map(([v, l]) => (
                  <div key={l} className="border border-[#2E333D] bg-[#141619] rounded-xl p-4 text-center">
                    <p className="font-display text-3xl font-extrabold text-[#FFC700]">{v}</p>
                    <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 mt-1">{l}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-2.5">
                <p className="flex items-center gap-2.5 text-sm text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" /> 7 véhicules avec stock OK</p>
                <p className="flex items-center gap-2.5 text-sm text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-amber-400" /> 3 véhicules à réapprovisionner</p>
                <p className="flex items-center gap-2.5 text-sm text-gray-300"><span className="w-2.5 h-2.5 rounded-full bg-red-400" /> 2 véhicules avec plusieurs références critiques</p>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Véhicule", "Technicien", "Référence", "Stock faible", "Stock critique"].map((f) => (
                  <span key={f} className="border border-[#2E333D] bg-[#141619] rounded-full px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">{f}</span>
                ))}
              </div>
              <p className="mt-5 text-[11px] text-gray-600">Exemple visuel — fonctionnalité prévue.</p>
            </div>
          </Reveal>
        </div>

        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="h-full border border-[#2E333D] bg-[#0C0D0E] rounded-2xl p-6 sm:p-8" data-testid="fleet-hierarchy">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFC700] mb-6">Architecture prévue</p>
              <div className="flex flex-wrap items-center gap-y-3">
                {HIERARCHIE.map((h, i) => (
                  <span key={h} className="flex items-center">
                    <span className={`font-display font-bold uppercase tracking-wide ${i === HIERARCHIE.length - 1 ? "text-[#FFC700]" : "text-[#F9FAFB]"} ${i === 3 ? "border border-[#FFC700]/50 rounded-lg px-2.5 py-1" : ""}`}>{h}</span>
                    {i < HIERARCHIE.length - 1 && <ChevronRight size={16} className="text-gray-600 mx-1.5" />}
                  </span>
                ))}
              </div>
              <p className="mt-6 text-sm text-gray-400 leading-relaxed">
                Une STOCKBOX 8 pourra être associée à une entreprise, un véhicule ou un technicien —
                et l'association pourra être modifiée si une boîte est déplacée d'un véhicule à un autre.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="h-full border border-[#2E333D] bg-[#0C0D0E] rounded-2xl p-6 sm:p-8" data-testid="fleet-history">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFC700] mb-6 flex items-center gap-2"><History size={14} /> Historique prévu</p>
              <div className="space-y-0">
                {HISTORIQUE.map((h, i) => (
                  <div key={i} className="flex gap-4 pb-5 relative" data-testid={`fleet-history-${i}`}>
                    <div className="flex flex-col items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#FFC700] mt-1.5" />
                      {i < HISTORIQUE.length - 1 && <span className="w-px flex-1 bg-[#2E333D] mt-1" />}
                    </div>
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{h.date} — {h.qui}</p>
                      <p className="text-sm text-gray-300 mt-0.5">{h.texte}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-600">Tracer qui a fait quoi, quand, et dans quel véhicule — exemple visuel.</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.05}>
          <div className="mt-16 text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-[#F9FAFB]"
              data-testid="fleet-hook-1"
            >
              Un seul écran pour <span className="text-[#FFC700]">toute votre flotte</span>
            </motion.p>
            <p className="mt-5 text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Plusieurs véhicules ? Plusieurs techniciens ? À terme, STOCKBOX 8 pourra permettre de centraliser
              le suivi des petites pièces présentes dans vos véhicules, d'identifier les stocks faibles et de
              préparer les réapprovisionnements depuis une seule application.
            </p>
            <p className="mt-10 font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-[#F9FAFB]" data-testid="fleet-hook-2">
              Du stock <span className="text-[#FFC700]">au réapprovisionnement</span>
            </p>
            <p className="mt-4 text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Ne vous contentez plus de constater qu'une référence manque. L'objectif est de pouvoir identifier
              les besoins, préparer une liste de réapprovisionnement et suivre son traitement jusqu'au retour
              du stock dans le véhicule.
            </p>
            <p className="mt-8 font-mono text-xs uppercase tracking-widest text-gray-500" data-testid="fleet-disclaimer">
              Fonctionnalités prévues — vision produit en développement. Aucune de ces fonctions n'est actuellement disponible.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
